import { createContext, useContext } from 'react';
import type { Locale } from '@med/shared';

/**
 * Interface strings. Clinical CONTENT is never translated here — it is stored
 * per locale and shown exactly as a reviewer entered it. This file covers
 * labels, buttons and messages only.
 */
const EN = {
  appName: 'Medication Catalogue',
  skipToContent: 'Skip to main content',
  loading: 'Loading…',
  retry: 'Try again',
  close: 'Close',
  cancel: 'Cancel',
  save: 'Save',
  required: 'required',
  language: 'Language',
  switchToHebrew: 'עברית',
  switchToEnglish: 'English',
  theme: 'Theme',
  switchToDark: 'Dark',
  switchToLight: 'Light',

  // Navigation
  navExplore: 'Explore',
  navCatalogue: 'Catalogue',
  navSaved: 'Saved',
  navAdministration: 'Administration',
  navReview: 'Review',
  navImports: 'Imports',
  navUsers: 'Users',
  navAudit: 'Audit log',
  navSignOut: 'Sign out',
  navAccount: 'Account',
  mainNavigation: 'Main navigation',

  // Explore (home)
  exploreEyebrow: 'Medication reference',
  exploreQuestion: 'What are you looking for?',
  exploreByArea: 'Explore by clinical area',
  viewAll: 'View all',
  recentlyViewed: 'Recently viewed',
  suggestedSearches: 'Try',
  browseAll: 'Browse the full catalogue',
  bookmarkAdd: 'Save',
  bookmarkRemove: 'Remove from saved',
  savedEmpty: 'Nothing saved yet.',
  savedEmptyHint: 'Save a medication from its page to find it here quickly.',

  // Auth
  signIn: 'Sign in',
  signInHeading: 'Sign in to the catalogue',
  email: 'Email address',
  password: 'Password',
  currentPassword: 'Current password',
  newPassword: 'New password',
  confirmPassword: 'Confirm new password',
  forgotPassword: 'Forgotten your password?',
  forgotHeading: 'Reset your password',
  forgotHelp: 'Enter your email address and we will send you a reset link.',
  sendResetLink: 'Send reset link',
  resetHeading: 'Choose a new password',
  setPassword: 'Set password',
  acceptHeading: 'Set up your account',
  acceptHelp: 'Choose a password to finish setting up your account.',
  acceptOwnEmailHint: 'This is how you will sign in, and where a password reset would go.',
  fullName: 'Full name',
  inviteByLink: 'Create a group link',
  inviteByLinkHelp:
    'One link for a whole group, with no address on it. Everyone who opens it gives their own name and email. It stays open for 30 days — anyone it is forwarded to can join too, so revoke it once the group is in.',
  inviteLinkCreated: 'Group link created',
  inviteLinkNote:
    'Anyone who opens this can create an account, until it expires or you revoke it below. Paste it once into the group.',
  inviteLinkCopyNow:
    'Copy it before you leave this page. Only a fingerprint of it is stored, so it cannot be shown again — if you lose it, revoke this link and make another.',
  inviteLinksTitle: 'Open group links',
  inviteLinksNone: 'No group links are open.',
  inviteLinkJoined: '{n} joined',
  inviteLinkExpires: 'expires {when}',
  inviteLinkExpired: 'expired',
  inviteLinkRevoked: 'revoked',
  inviteLinkRevoke: 'Revoke',
  inviteLinkRevokeConfirm: 'Stop this link working? Accounts already created are not affected.',
  accessByInvitation: 'Accounts are created by an administrator. There is no public registration.',
  mfaHeading: 'Two-factor authentication',
  mfaHelp: 'Enter the 6-digit code from your authenticator app.',
  mfaCode: 'Authentication code',
  mfaVerify: 'Verify',
  mfaEnrollHeading: 'Set up two-factor authentication',
  mfaEnrollHelp:
    'Your role requires two-factor authentication. Scan this code with an authenticator app, then enter the code it shows.',
  mfaSecretManual: 'Or enter this key manually:',
  mfaOpenAuthenticator: 'Open my authenticator app',
  mfaOpenAuthenticatorHint:
    'Adds the account automatically, then come back here and enter the 6-digit code it shows.',
  mfaOtherDevice: 'Setting it up on another device instead',
  mfaCodeHint: 'Six digits from your authenticator app — not the setup key above.',
  mfaRecoveryHeading: 'Save your recovery codes',
  mfaRecoveryHelp:
    'Each code can be used once if you lose your authenticator. Store them somewhere safe — they will not be shown again.',
  mfaRecoveryAcknowledge: 'I have saved these codes',
  passwordsDoNotMatch: 'The two passwords do not match.',
  passwordMinLength: 'Use at least {n} characters.',
  signedInAs: 'Signed in as',
  changePassword: 'Change password',
  passwordChanged: 'Your password has been changed.',

  // Search
  searchLabel: 'Search medications',
  searchPlaceholder: 'Generic name, trade name, class, indication…',
  searchPlaceholderShort: 'Search medications…',
  searchHint: 'Search by generic name, trade name, group, indication, mechanism or keyword.',
  searchSubmit: 'Search',
  clearSearch: 'Clear search',
  resultsCount: (n: number) => (n === 1 ? '1 medication' : `${n} medications`),
  noResults: 'No medications matched your search.',
  noResultsHint: 'Check the spelling, or try a generic name, trade name or drug class.',
  approximateMatch: 'No exact match. Showing closest results for',
  filters: 'Filters',
  showFilters: 'Show filters',
  hideFilters: 'Hide filters',
  clearFilters: 'Clear all filters',
  activeFilters: 'Active filters',
  filterTherapeuticGroup: 'Therapeutic group',
  filterDrugClass: 'Drug class',
  filterDrugFamily: 'Drug family',
  filterFormulation: 'Formulation',
  loadMore: 'Load more',
  resultsRegion: 'Search results',
  menu: 'Menu',
  closeFilters: 'Close filters',
  removeFilterValue: (label: string) => `Remove filter: ${label}`,
  evaluationDetails: 'Details',
  evaluationHide: 'Hide',
  compareSelectedCount: (n: number) => (n === 1 ? '1 medication selected' : `${n} medications selected`),

  // Detail
  detailRegion: 'Medication details',
  openDetails: 'Open details for',
  backToResults: 'Back to results',
  recordFields: 'Record fields',
  usageTitle: 'Usage',
  usageIntro: 'Who has an account, who is coming back, and where people get stuck.',
  usageActiveAccounts: 'active accounts',
  usageSignedIn7d: 'signed in this week',
  usagePendingInvites: 'invitations not accepted',
  usageNeverSignedIn: 'never signed in',
  usageLastFortnight: 'Sign-ins, last two weeks',
  usagePeople: 'People',
  usageName: 'Person',
  usageLastSeen: 'Last signed in',
  usageLastActive: 'Last active',
  usageSignIns30d: 'Sign-ins',
  usageSearches30d: 'Searches',
  usageViews30d: 'Opened',
  usageEmptySearches30d: 'Found nothing',
  usageTrouble: 'Trouble',
  usageDevice: 'Device',
  usageLast30d: 'Last 30 days',
  usageFailedSignIns: '{n} failed sign-ins',
  usageLockouts: '{n} lockouts',
  usageGapsTitle: 'Searched for, found nothing',
  usageGapsIntro: 'Terms that returned no result. Each one names something the catalogue does not have, or a spelling it does not recognise.',
  usageGapsNone: 'Every search in the last 30 days returned something.',
  usageTerm: 'Search term',
  usageTimes: 'Times',
  usagePeopleCount: 'People',
  usagePopularTitle: 'Most opened',
  usagePopularNone: 'No records opened yet.',
  usageRecord: 'Record',
  usageOpened: 'Opened',
  usageToday: 'today',
  usageYesterday: 'yesterday',
  usageDaysAgo: '{n} days ago',
  usageNotTracked: 'Sign-ins, searches and records opened are recorded for each account so that problems can be found and fixed. This page shows how much each person used the tool, not a record of what they read.',
  sources: 'Sources',
  sourceLabel: 'Source',
  page: 'Page',
  jurisdiction: 'Jurisdiction',
  approvalStatus: 'Approval status',
  reviewDate: 'Review date',
  noSources: 'No source has been attached to this field yet.',
  validationStatus: 'Validation status',
  lastPublished: 'Published',
  versionLabel: 'Version',
  showingOtherLanguage: 'Shown in English',
  showingOtherLanguageHe: 'Shown in Hebrew',

  // Compare
  compare: 'Compare',
  compareAdd: 'Add to comparison',
  compareRemove: 'Remove from comparison',
  compareOpen: 'Compare selected',
  compareHeading: 'Comparison',
  compareTrayLabel: 'Selected for comparison',
  compareLimit: (n: number) => `You can compare up to ${n} medications.`,
  compareEmpty: 'Select at least two medications to compare.',
  compareClear: 'Clear comparison',

  // Disclaimer / legal
  disclaimerShort:
    'A professional reference aid. It does not replace clinical judgement or current prescribing information.',
  disclaimerHeading: 'Important',
  evaluationHeading: 'Evaluation copy —',
  unvalidatedRecord: 'Not clinically reviewed',
  unvalidatedRecordDetail:
    'This record was published for evaluation before clinical review was complete. It has not been checked against an authoritative source. Do not use it for clinical decisions.',
  outstandingChecks: 'Outstanding checks',
  sourceLookupHeading: 'Find a source',
  sourceLookupIntro:
    'Where this medication is documented in external registries. Nothing is attached until you read the document and confirm it.',
  sourceLookupNone: 'No documents found for this medication.',
  sourceLookupUnavailable: (provider: string) => `${provider} could not be checked.`,
  sourceLookupOpen: 'Read it',
  sourceLookupUse: 'Use this',
  sourceLookupAttach: 'Attach as a source',
  sourceLookupField: 'Which claim does it support?',
  sourceLookupSection: 'Section or page',
  sourceLookupSectionHint: 'Where in the document the claim appears, for example 4.2.',
  sourceLookupJurisdictionHint: 'The jurisdiction you are claiming this for — not the document\u2019s.',
  sourceLookupStatusHint:
    'Whether the use is approved in that jurisdiction. The document cannot answer this for you.',
  sourceLookupJurisdictionWarning: (jurisdiction: string) =>
    `This document governs ${jurisdiction}. It is evidence about the medicine, not about what is approved elsewhere. Confirm the status for your own jurisdiction before attaching it.`,
  sourceLookupConfirm: 'Attach source',
  sourceLookupResponsibility: 'This citation will be recorded against your account.',
  privacyPolicy: 'Privacy Policy',
  terms: 'Terms of Use',
  contact: 'Contact',
  pendingApproval: 'Awaiting approval from the system owner.',
  noPatientData: 'Do not enter patient-identifiable information anywhere in this system.',

  // Errors
  errorGeneric: 'Something went wrong. Please try again.',
  errorNotFound: 'That page could not be found.',
  errorForbidden: 'You do not have permission to view that.',
  errorOffline: 'Cannot reach the server. Check your connection.',
  sessionExpired: 'Your session has expired. Please sign in again.',
} as const;


const HE = {
  appName: 'קטלוג תרופות',
  skipToContent: 'דילוג לתוכן הראשי',
  loading: 'טוען…',
  retry: 'נסה שוב',
  close: 'סגירה',
  cancel: 'ביטול',
  save: 'שמירה',
  required: 'שדה חובה',
  language: 'שפה',
  switchToHebrew: 'עברית',
  switchToEnglish: 'English',
  theme: 'ערכת נושא',
  switchToDark: 'כהה',
  switchToLight: 'בהיר',

  navExplore: 'עיון',
  navCatalogue: 'קטלוג',
  navSaved: 'שמורים',
  navAdministration: 'ניהול',
  navReview: 'בדיקה',
  navImports: 'ייבוא',
  navUsers: 'משתמשים',
  navAudit: 'יומן ביקורת',
  navSignOut: 'התנתקות',
  navAccount: 'החשבון שלי',
  mainNavigation: 'ניווט ראשי',

  exploreEyebrow: 'מדריך תרופות',
  exploreQuestion: 'מה מחפשים?',
  exploreByArea: 'עיון לפי תחום קליני',
  viewAll: 'הצג הכול',
  recentlyViewed: 'נצפו לאחרונה',
  suggestedSearches: 'נסו',
  browseAll: 'עיון בקטלוג המלא',
  bookmarkAdd: 'שמירה',
  bookmarkRemove: 'הסרה מהשמורים',
  savedEmpty: 'עדיין לא שמרת דבר.',
  savedEmptyHint: 'שמרו תרופה מתוך העמוד שלה כדי למצוא אותה כאן במהירות.',

  signIn: 'כניסה',
  signInHeading: 'כניסה לקטלוג',
  email: 'כתובת אימייל',
  password: 'סיסמה',
  currentPassword: 'סיסמה נוכחית',
  newPassword: 'סיסמה חדשה',
  confirmPassword: 'אימות סיסמה חדשה',
  forgotPassword: 'שכחת סיסמה?',
  forgotHeading: 'איפוס סיסמה',
  forgotHelp: 'הזן את כתובת האימייל שלך ונשלח קישור לאיפוס.',
  sendResetLink: 'שליחת קישור לאיפוס',
  resetHeading: 'בחירת סיסמה חדשה',
  setPassword: 'קביעת סיסמה',
  acceptHeading: 'הגדרת החשבון',
  acceptHelp: 'בחר סיסמה כדי להשלים את הגדרת החשבון.',
  acceptOwnEmailHint: 'בכתובת הזו תתחבר, ואליה יישלח איפוס סיסמה.',
  fullName: 'שם מלא',
  inviteByLink: 'צור קישור קבוצתי',
  inviteByLinkHelp:
    'קישור אחד לקבוצה שלמה, בלי כתובת מייל. כל מי שפותח אותו מזין את השם והמייל שלו. הוא נשאר פתוח 30 יום — גם מי שיקבל אותו בהעברה יוכל להצטרף, אז כדאי לבטל אותו אחרי שהקבוצה נכנסה.',
  inviteLinkCreated: 'נוצר קישור קבוצתי',
  inviteLinkNote:
    'כל מי שפותח את הקישור יכול ליצור חשבון, עד שהוא פג או עד שתבטל אותו למטה. מדביקים אותו פעם אחת בקבוצה.',
  inviteLinkCopyNow:
    'העתק אותו לפני שתעזוב את הדף. נשמרת רק טביעת אצבע שלו, אז אי אפשר להציג אותו שוב — אם איבדת אותו, בטל את הקישור הזה וצור חדש.',
  inviteLinksTitle: 'קישורים קבוצתיים פתוחים',
  inviteLinksNone: 'אין קישורים קבוצתיים פתוחים.',
  inviteLinkJoined: '{n} הצטרפו',
  inviteLinkExpires: 'פג {when}',
  inviteLinkExpired: 'פג תוקף',
  inviteLinkRevoked: 'בוטל',
  inviteLinkRevoke: 'בטל',
  inviteLinkRevokeConfirm: 'להפסיק את פעולת הקישור? חשבונות שכבר נוצרו לא יושפעו.',
  accessByInvitation: 'החשבונות נוצרים בידי מנהל המערכת. אין הרשמה ציבורית.',
  mfaHeading: 'אימות דו-שלבי',
  mfaHelp: 'הזן את הקוד בן 6 הספרות מאפליקציית האימות.',
  mfaCode: 'קוד אימות',
  mfaVerify: 'אימות',
  mfaEnrollHeading: 'הגדרת אימות דו-שלבי',
  mfaEnrollHelp:
    'התפקיד שלך מחייב אימות דו-שלבי. סרוק את הקוד באפליקציית אימות והזן את הקוד שמוצג בה.',
  mfaSecretManual: 'לחלופין, הזן מפתח זה ידנית:',
  mfaOpenAuthenticator: 'פתיחת אפליקציית האימות',
  mfaOpenAuthenticatorHint:
    'החשבון יתווסף אוטומטית. חזור לכאן והזן את הקוד בן 6 הספרות שמוצג בה.',
  mfaOtherDevice: 'הגדרה במכשיר אחר',
  mfaCodeHint: 'שש ספרות מאפליקציית האימות — לא המפתח שמופיע למעלה.',
  mfaRecoveryHeading: 'שמור את קודי השחזור',
  mfaRecoveryHelp:
    'כל קוד ניתן לשימוש פעם אחת אם אפליקציית האימות אינה זמינה. שמור אותם במקום בטוח — הם לא יוצגו שוב.',
  mfaRecoveryAcknowledge: 'שמרתי את הקודים',
  passwordsDoNotMatch: 'הסיסמאות אינן זהות.',
  passwordMinLength: 'יש להשתמש ב-{n} תווים לפחות.',
  signedInAs: 'מחובר כ-',
  changePassword: 'שינוי סיסמה',
  passwordChanged: 'הסיסמה שונתה.',

  searchLabel: 'חיפוש תרופות',
  searchPlaceholder: 'שם גנרי, שם מסחרי, קבוצה, התוויה…',
  searchPlaceholderShort: 'חיפוש תרופה…',
  searchHint: 'ניתן לחפש לפי שם גנרי, שם מסחרי, קבוצה, התוויה, מנגנון או מילת מפתח.',
  searchSubmit: 'חיפוש',
  clearSearch: 'ניקוי החיפוש',
  resultsCount: (n: number) => (n === 1 ? 'תרופה אחת' : `${n} תרופות`),
  noResults: 'לא נמצאו תרופות התואמות לחיפוש.',
  noResultsHint: 'בדוק את האיות, או נסה שם גנרי, שם מסחרי או מחלקת תרופות.',
  approximateMatch: 'לא נמצאה התאמה מדויקת. מוצגות התוצאות הקרובות ביותר עבור',
  filters: 'מסננים',
  showFilters: 'הצגת מסננים',
  hideFilters: 'הסתרת מסננים',
  clearFilters: 'ניקוי כל המסננים',
  activeFilters: 'מסננים פעילים',
  filterTherapeuticGroup: 'קבוצה טיפולית',
  filterDrugClass: 'מחלקת תרופות',
  filterDrugFamily: 'משפחת תרופות',
  filterFormulation: 'צורת מתן',
  loadMore: 'טעינת עוד',
  resultsRegion: 'תוצאות החיפוש',
  menu: 'תפריט',
  closeFilters: 'סגירת מסננים',
  removeFilterValue: (label: string) => `הסרת מסנן: ${label}`,
  evaluationDetails: 'פרטים',
  evaluationHide: 'הסתרה',
  compareSelectedCount: (n: number) => (n === 1 ? 'נבחרה תרופה אחת' : `נבחרו ${n} תרופות`),

  detailRegion: 'פרטי התרופה',
  openDetails: 'פתיחת פרטים עבור',
  backToResults: 'חזרה לתוצאות',
  recordFields: 'שדות הרשומה',
  usageTitle: 'שימוש',
  usageIntro: 'למי יש חשבון, מי חוזר להשתמש, ואיפה משתמשים נתקעים.',
  usageActiveAccounts: 'חשבונות פעילים',
  usageSignedIn7d: 'התחברו השבוע',
  usagePendingInvites: 'הזמנות שטרם נענו',
  usageNeverSignedIn: 'מעולם לא התחברו',
  usageLastFortnight: 'התחברויות, שבועיים אחרונים',
  usagePeople: 'משתמשים',
  usageName: 'משתמש',
  usageLastSeen: 'התחברות אחרונה',
  usageLastActive: 'פעילות אחרונה',
  usageSignIns30d: 'התחברויות',
  usageSearches30d: 'חיפושים',
  usageViews30d: 'תרופות שנפתחו',
  usageEmptySearches30d: 'ללא תוצאות',
  usageTrouble: 'תקלות',
  usageDevice: 'מכשיר',
  usageLast30d: '30 הימים האחרונים',
  usageFailedSignIns: '{n} התחברויות שנכשלו',
  usageLockouts: '{n} חסימות',
  usageGapsTitle: 'חיפושים שלא החזירו תוצאה',
  usageGapsIntro: 'מונחים שחיפשו ולא נמצאו. כל אחד מהם מצביע על תוכן שחסר בקטלוג, או על כתיב שהמערכת לא מזהה.',
  usageGapsNone: 'כל החיפושים ב-30 הימים האחרונים החזירו תוצאות.',
  usageTerm: 'מונח החיפוש',
  usageTimes: 'פעמים',
  usagePeopleCount: 'משתמשים',
  usagePopularTitle: 'התרופות שנפתחו הכי הרבה',
  usagePopularNone: 'עדיין לא נפתחו תרופות.',
  usageRecord: 'תרופה',
  usageOpened: 'נפתחה',
  usageToday: 'היום',
  usageYesterday: 'אתמול',
  usageDaysAgo: 'לפני {n} ימים',
  usageNotTracked: 'התחברויות, חיפושים ותרופות שנפתחו נרשמים לכל חשבון, כדי שנוכל לאתר תקלות ולתקן אותן. הדף הזה מציג כמה כל אחד השתמש בכלי, ולא רישום של מה שקרא.',
  sources: 'מקורות',
  sourceLabel: 'מקור',
  page: 'עמוד',
  jurisdiction: 'תחום שיפוט',
  approvalStatus: 'סטטוס אישור',
  reviewDate: 'תאריך בדיקה',
  noSources: 'טרם צורף מקור לשדה זה.',
  validationStatus: 'סטטוס אימות',
  lastPublished: 'פורסם',
  versionLabel: 'גרסה',
  showingOtherLanguage: 'מוצג באנגלית',
  showingOtherLanguageHe: 'מוצג בעברית',

  compare: 'השוואה',
  compareAdd: 'הוספה להשוואה',
  compareRemove: 'הסרה מההשוואה',
  compareOpen: 'השוואת הנבחרות',
  compareHeading: 'השוואה',
  compareTrayLabel: 'נבחרו להשוואה',
  compareLimit: (n: number) => `ניתן להשוות עד ${n} תרופות.`,
  compareEmpty: 'יש לבחור לפחות שתי תרופות להשוואה.',
  compareClear: 'ניקוי ההשוואה',

  disclaimerShort:
    'כלי עזר מקצועי. אינו מחליף שיקול דעת קליני או מידע מרשם עדכני.',
  disclaimerHeading: 'חשוב',
  evaluationHeading: 'גרסת הערכה —',
  unvalidatedRecord: 'לא עבר בדיקה קלינית',
  unvalidatedRecordDetail:
    'רשומה זו פורסמה לצורכי הערכה לפני השלמת הבדיקה הקלינית. היא לא הושוותה למקור מוסמך. אין להסתמך עליה לקבלת החלטות קליניות.',
  outstandingChecks: 'בדיקות שטרם הושלמו',
  sourceLookupHeading: 'איתור מקור',
  sourceLookupIntro:
    'היכן התרופה מתועדת במאגרים חיצוניים. שום דבר לא מצורף עד שתקרא את המסמך ותאשר.',
  sourceLookupNone: 'לא נמצאו מסמכים עבור תרופה זו.',
  sourceLookupUnavailable: (provider: string) => `לא ניתן היה לבדוק את ${provider}.`,
  sourceLookupOpen: 'קריאה',
  sourceLookupUse: 'בחירה',
  sourceLookupAttach: 'צירוף כמקור',
  sourceLookupField: 'לאיזו טענה המקור מתייחס?',
  sourceLookupSection: 'סעיף או עמוד',
  sourceLookupSectionHint: 'היכן במסמך מופיעה הטענה, למשל 4.2.',
  sourceLookupJurisdictionHint: 'תחום השיפוט שעבורו אתה טוען זאת — לא זה של המסמך.',
  sourceLookupStatusHint:
    'האם השימוש מאושר באותו תחום שיפוט. המסמך אינו יכול לענות על כך עבורך.',
  sourceLookupJurisdictionWarning: (jurisdiction: string) =>
    `מסמך זה חל על ${jurisdiction}. הוא ראיה על התרופה, לא על מה שמאושר במקום אחר. ודא את הסטטוס בתחום השיפוט שלך לפני הצירוף.`,
  sourceLookupConfirm: 'צירוף המקור',
  sourceLookupResponsibility: 'הציטוט יירשם על שמך.',
  privacyPolicy: 'מדיניות פרטיות',
  terms: 'תנאי שימוש',
  contact: 'יצירת קשר',
  pendingApproval: 'ממתין לאישור בעל המערכת.',
  noPatientData: 'אין להזין פרטים מזהים של מטופלים בשום מקום במערכת.',

  errorGeneric: 'אירעה שגיאה. נסה שוב.',
  errorNotFound: 'הדף לא נמצא.',
  errorForbidden: 'אין לך הרשאה לצפות בכך.',
  errorOffline: 'לא ניתן להגיע לשרת. בדוק את החיבור.',
  sessionExpired: 'תוקף החיבור פג. יש להתחבר מחדש.',
};

/**
 * Widens the English literal types into plain strings, so the Hebrew table is
 * checked for having exactly the same keys and shapes without every value
 * having to be the identical literal.
 */
type Widen<T> = {
  [K in keyof T]: T[K] extends (...args: infer A) => infer R ? (...args: A) => R : string;
};

export type Strings = Widen<typeof EN>;

export const STRINGS: Record<Locale, Strings> = { en: EN, he: HE };



export interface I18nValue {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  t: Strings;
  setLocale(locale: Locale): void;
}

export const I18nContext = createContext<I18nValue | null>(null);

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n must be used inside the I18n provider');
  return value;
}

const STORAGE_KEY = 'medcat.locale';

export function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'he' || stored === 'en') return stored;
  } catch {
    // Storage can be unavailable (private mode); fall through to the default.
  }
  return navigator.language?.startsWith('he') ? 'he' : 'en';
}

export function storeLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // A stored preference is a convenience, not a requirement.
  }
}
